/**
 * Per-pass draw and triangle split (perf pass PF-0, plan `docs/plans/2026-10-11-perf-pass.md` §3.1). Test builds wrap
 * `renderer.render` once (the D-571 pattern, `pipelineCount.ts`: throws if the method is missing). Every call pushes
 * three's running `info.render.drawCalls`/`triangles` onto a preallocated stack; on exit the call's delta *minus its
 * nested children* goes to one bucket, so a shadow level rendered inside the scene pass (`ShadowNode.updateBefore`)
 * and a scene pass rendered inside its post quad (`PassNode.updateBefore` under `QuadMesh.render`) each count once.
 *
 * Classification, first match wins (checked against three r186 in `passStats.test.ts`):
 * 1. the camera is shadow level `i`'s `shadow.camera` (the scene's `sun` → `shadow.shadowNode.levels`) → `shadow<i>`;
 * 2. `scene.overrideMaterial.name === 'post.prepassNormal'` (the AO prepass, `post/ao.ts`) → `prepass`;
 * 3. `scene.isQuadMesh` (post quads) → `post`;
 * 4. `scene.name === 'scaled'` (the scaled-space pass, `scaled/index.ts`) → `scaled`;
 * 5. the main camera → `scene`;
 * 6. anything else (sky bake, PMREM, warm-ups) → `offscreen`.
 *
 * Totals reset when `renderer.info.frame` changes (three sets it each animation frame after `info.reset()`), so
 * between frames they cover the last whole frame, as `__debug.renderStats()` does. Nothing here allocates per frame.
 */
import { z } from 'zod'

export const PASS_NAMES = ['shadow0', 'shadow1', 'shadow2', 'shadow3', 'prepass', 'scene', 'post', 'scaled', 'offscreen'] as const
export type PassName = (typeof PASS_NAMES)[number]

const SHADOW_SLOTS = 4
const PREPASS = 4
const SCENE = 5
const POST = 6
const SCALED = 7
const OFFSCREEN = 8
/** Nesting depth tracked (deeper renders count toward their parent). */
const MAX_DEPTH = 8

/** The prepass override material's name (`post/ao.ts`). */
export const PREPASS_MATERIAL = 'post.prepassNormal'
/** The scaled-space scene's name (`scaled/index.ts`). */
export const SCALED_SCENE = 'scaled'

/** One pass's draws and triangles. */
export interface PassCount {
  draws: number
  tris: number
}

/** A frame's split, filled by `read` (caller-owned, reused). */
export interface PassFrame {
  /** `renderer.info.frame` the split belongs to. */
  frame: number
  passes: Record<PassName, PassCount>
}

/** A zeroed `PassFrame` to pass to `read`. */
export function createPassFrame(): PassFrame {
  const passes = {} as Record<PassName, PassCount>
  for (const n of PASS_NAMES) passes[n] = { draws: 0, tris: 0 }
  return { frame: -1, passes }
}

/** What the wrapper reads of a render call's scene (an `Object3D`: a `Scene` or a `QuadMesh`). */
interface RenderedScene {
  readonly name: string
  readonly isQuadMesh?: boolean
  readonly overrideMaterial?: { readonly name: string } | null
}

/** What the wrapper reads of the renderer (three's `Renderer`). */
export interface PassStatsRenderer {
  render: (scene: never, camera: never) => unknown
  readonly info: { readonly frame: number; readonly render: { readonly drawCalls: number; readonly triangles: number } }
}

/** What `findShadowCameras` walks: the scene's `sun` light and its clipmap shadow node (`environment/shadows.ts`). */
interface SceneWithSun {
  getObjectByName(name: string): unknown
}

type ShadowNodeLike = { levels?: readonly { shadow: { camera: object } }[] }

/** The shadow level cameras, level order (empty when there is no `sun` with a clipmap shadow node). */
export function findShadowCameras(scene: SceneWithSun): object[] {
  const sun = scene.getObjectByName('sun') as { shadow?: { shadowNode?: ShadowNodeLike } } | undefined
  const levels = sun?.shadow?.shadowNode?.levels
  if (!levels) return []
  return levels.slice(0, SHADOW_SLOTS).map((l) => l.shadow.camera)
}

export interface PassStats {
  /** Copies the latest frame's split into `out` (no allocation). */
  read(out: PassFrame): PassFrame
  /**
   * PERF L-M4: arms a one-shot listing of every scene-pass render list of the next whole frame (`listRenderFamilies`,
   * one entry per main-camera render). Nothing runs until armed; read the result with `families()`.
   */
  armFamilies(): void
  /** The last armed frame's listings, one per scene-pass render (null before one completes). */
  families(): SceneFamilies | null
  /** Re-resolves the shadow level cameras (preset change). */
  resolve(): void
}

/**
 * Wraps `renderer.render`. `mainCamera` is the game camera; `scene` holds the `sun` (shadow cameras resolved lazily
 * on the first render that finds them, and again on `resolve()`). Throws if `render` is missing.
 */
export function createPassStats(renderer: PassStatsRenderer, scene: SceneWithSun, mainCamera: object): PassStats {
  const target = renderer as unknown as Record<string, unknown>
  const original = target.render
  if (typeof original !== 'function') throw new Error('createPassStats: the renderer has no render()')
  const inner = original as (this: unknown, scene: unknown, camera: unknown) => unknown
  const info = renderer.info

  const draws = new Float64Array(PASS_NAMES.length)
  const tris = new Float64Array(PASS_NAMES.length)
  const startDraws = new Float64Array(MAX_DEPTH)
  const startTris = new Float64Array(MAX_DEPTH)
  const childDraws = new Float64Array(MAX_DEPTH)
  const childTris = new Float64Array(MAX_DEPTH)
  const shadowCams: (object | null)[] = [null, null, null, null]
  let resolved = false
  // L-M4 listing: 0 off, 1 armed (waits for a new frame), 2 collecting this frame.
  let armState = 0
  let collecting: RenderFamily[][] = []
  let listed: SceneFamilies | null = null
  let depth = 0
  let frame = -1

  const resolve = (): void => {
    const cams = findShadowCameras(scene)
    for (let i = 0; i < SHADOW_SLOTS; i++) shadowCams[i] = cams[i] ?? null
    resolved = cams.length > 0
  }

  const classify = (s: RenderedScene, camera: unknown): number => {
    for (let i = 0; i < SHADOW_SLOTS; i++) if (shadowCams[i] !== null && shadowCams[i] === camera) return i
    if (s.overrideMaterial?.name === PREPASS_MATERIAL) return PREPASS
    if (s.isQuadMesh === true) return POST
    if (s.name === SCALED_SCENE) return SCALED
    if (camera === mainCamera) return SCENE
    return OFFSCREEN
  }

  target.render = function wrappedRender(this: unknown, s: RenderedScene, camera: unknown): unknown {
    if (depth >= MAX_DEPTH) return inner.call(this, s, camera)
    if (depth === 0) {
      if (info.frame !== frame) {
        frame = info.frame
        if (armState === 2) {
          listed = { sceneDraws: draws[SCENE], renders: collecting }
          armState = 0
        } else if (armState === 1) armState = 2
        draws.fill(0)
        tris.fill(0)
      }
      if (!resolved) resolve()
    }
    const d = depth
    startDraws[d] = info.render.drawCalls
    startTris[d] = info.render.triangles
    childDraws[d] = 0
    childTris[d] = 0
    depth++
    try {
      return inner.call(this, s, camera)
    } finally {
      depth = d
      const dd = info.render.drawCalls - startDraws[d]
      const dt = info.render.triangles - startTris[d]
      const bucket = classify(s, camera)
      if (armState === 2 && bucket === SCENE) collecting.push(listRenderFamilies(sceneRenderList(renderer, s, camera)))
      draws[bucket] += dd - childDraws[d]
      tris[bucket] += dt - childTris[d]
      if (d > 0) {
        childDraws[d - 1] += dd
        childTris[d - 1] += dt
      }
    }
  }

  return {
    read(out: PassFrame): PassFrame {
      out.frame = frame
      for (let i = 0; i < PASS_NAMES.length; i++) {
        const p = out.passes[PASS_NAMES[i]]
        p.draws = draws[i]
        p.tris = tris[i]
      }
      return out
    },
    resolve,
    armFamilies(): void {
      armState = 1
      collecting = []
    },
    families: () => listed,
  }
}

// --- PERF L-M4: the scene pass's render list grouped by family (test builds, on demand) ---

/** What `listRenderFamilies` reads of a three r186 render item (`RenderList.js`). */
export interface RenderItemLike {
  readonly object: { readonly name: string; readonly count?: number; readonly isInstancedMesh?: boolean }
  readonly geometry: { readonly index: { readonly count: number } | null; readonly attributes: { readonly position?: { readonly count: number } } }
  readonly material: { readonly name: string; readonly type: string }
}

/** What `listRenderFamilies` reads of a three r186 `RenderList`. */
export interface RenderListLike {
  readonly opaque: readonly RenderItemLike[]
  readonly transparent: readonly RenderItemLike[]
  readonly transparentDoublePass: readonly RenderItemLike[]
}

/** One family of render items: one material name (its type when unnamed), with counts and the commonest object names. */
export interface RenderFamily {
  material: string
  /** Render items (one draw each) in the family. */
  draws: number
  opaque: number
  transparent: number
  /** Triangles (index or position count / 3, times instances). */
  tris: number
  /** Up to 4 object names, commonest first, with counts. */
  objects: { name: string; count: number }[]
}

/** One armed frame's listing: the scene bucket's counted draws and one family list per scene-pass render. */
export interface SceneFamilies {
  sceneDraws: number
  renders: RenderFamily[][]
}

/** Families listed per object name (the rest are folded into the count). */
const FAMILY_OBJECT_NAMES = 4

/** Groups a render list's items by material name, biggest family first (ties by name). Pure; allocates (debug only). */
export function listRenderFamilies(list: RenderListLike): RenderFamily[] {
  const map = new Map<string, { fam: RenderFamily; names: Map<string, number> }>()
  const add = (item: RenderItemLike, transparent: boolean): void => {
    const key = item.material.name || item.material.type
    let e = map.get(key)
    if (!e) {
      e = { fam: { material: key, draws: 0, opaque: 0, transparent: 0, tris: 0, objects: [] }, names: new Map() }
      map.set(key, e)
    }
    e.fam.draws++
    if (transparent) e.fam.transparent++
    else e.fam.opaque++
    const verts = item.geometry.index?.count ?? item.geometry.attributes.position?.count ?? 0
    const instances = item.object.isInstancedMesh === true ? (item.object.count ?? 1) : 1
    e.fam.tris += Math.floor(verts / 3) * instances
    const n = item.object.name || '(unnamed)'
    e.names.set(n, (e.names.get(n) ?? 0) + 1)
  }
  for (const i of list.opaque) add(i, false)
  for (const i of list.transparentDoublePass) add(i, true)
  for (const i of list.transparent) add(i, true)
  const out: RenderFamily[] = []
  for (const { fam, names } of map.values()) {
    fam.objects = [...names.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, FAMILY_OBJECT_NAMES)
      .map(([name, count]) => ({ name, count }))
    out.push(fam)
  }
  return out.sort((a, b) => b.draws - a.draws || a.material.localeCompare(b.material))
}

/** The render list three r186 keeps for `(scene, camera)` (`renderer._renderLists`); throws if three renames it. */
function sceneRenderList(renderer: PassStatsRenderer, scene: unknown, camera: unknown): RenderListLike {
  const r = renderer as unknown as { _renderLists?: { get?: (s: unknown, c: unknown, l: unknown) => RenderListLike }; lighting?: unknown }
  const lists = r._renderLists
  if (!lists || typeof lists.get !== 'function') throw new Error('listRenderFamilies: the renderer has no _renderLists.get()')
  return lists.get(scene, camera, r.lighting)
}

/**
 * Live (cached) render and compute pipelines: three r186 keeps them in `renderer._pipelines.caches` (`Pipelines.js`,
 * deleted on release). Throws if the field is missing, so a three upgrade that renames it fails loudly.
 */
export function pipelinesLive(renderer: object): number {
  const caches = (renderer as { _pipelines?: { caches?: unknown } })._pipelines?.caches
  if (!(caches instanceof Map)) throw new Error('pipelinesLive: the renderer has no _pipelines.caches Map')
  return caches.size
}

// --- Per-pass ceilings (`passBudgets.json`) ---

const PassCeilingSchema = z.object({ draws: z.number().int().nonnegative(), tris: z.number().int().nonnegative() }).strict()
const SceneCeilingsSchema = z.object(Object.fromEntries(PASS_NAMES.map((n) => [n, PassCeilingSchema])) as Record<PassName, typeof PassCeilingSchema>).strict()

export const PassBudgetsSchema = z
  .object({
    scenes: z.record(z.string(), SceneCeilingsSchema),
    ships: z.object({ perShipDrawsSM: z.number().positive() }).strict(),
    pipelinesLive: z.number().int().positive(),
    measured: z
      .object({
        commit: z.string(),
        note: z.string(),
        scenes: z.record(z.string(), SceneCeilingsSchema.extend({ frame: PassCeilingSchema, pipelinesLive: z.number().int().nonnegative() }).partial()),
        pipelinesLive: z.number().int().nonnegative(),
        highRockSceneTris: z.number().int().nonnegative().nullable(),
      })
      .strict(),
  })
  .strict()

export type PassBudgets = z.infer<typeof PassBudgetsSchema>

export function parsePassBudgets(raw: unknown): PassBudgets {
  return PassBudgetsSchema.parse(raw)
}
